// The dashboard side of the parent<->iframe handshake (EMBEDDED_SSO.md §5).
// We ask the parent for a fresh handoff token and resolve with it. Origin is checked
// on BOTH ends: we post the request to every allowed origin in PARENT_ORIGINS (only the
// real parent, matched by the postMessage targetOrigin, actually receives it), and we
// accept a reply only if event.origin is one of PARENT_ORIGINS. Multi-origin so one
// built image works whether it's framed by the dev, uat, or prod Relius host. Works
// whether we're embedded (window.parent is the .NET page) or opened top-level
// (window.parent === window → no responder → times out).

// How often to re-send the request until the parent answers (see the send loop below).
const RETRY_MS = 500;

/**
 * Request a handoff token from the parent window.
 *
 * The reply may carry two optional extras alongside the token — both are additive, and
 * a parent that sends neither behaves exactly as before:
 *   • `sessionParameter` — the legacy portal's own session array, which we need to
 *     deep-link back into its .aspx pages (see legacySession.js; its localStorage copy
 *     is on the parent's origin and unreadable from here).
 *   • `capabilities` — what the parent supports, e.g. `['navigate']` (parentNavigation.js).
 *
 * @param {Object} opts
 * @param {string[]} opts.parentOrigins allowlist of parent origins (post + accept)
 * @param {number} opts.timeoutMs how long to wait before giving up
 * @returns {Promise<{handoffToken: string, sessionParameter: *, capabilities: string[]}>}
 */
export function requestParentToken({ parentOrigins, timeoutMs }) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const nonce = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : String(Date.now());

    let retry;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      clearInterval(retry);
    };

    const onMessage = (event) => {
      if (!parentOrigins.includes(event.origin)) return;    // trust only an allowed parent
      const data = event.data;
      if (!data || data.type !== 'pcs:auth:token') return;
      if (!data.handoffToken) return;
      settled = true;
      cleanup();
      resolve({
        handoffToken: data.handoffToken,
        sessionParameter: data.sessionParameter ?? null,
        capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
      });
    };

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      // Tagged so the UI can show "open from the portal" guidance for the common
      // case of someone loading the dashboard directly (no parent to answer).
      const err = new Error('Timed out waiting for the parent application to authenticate you.');
      err.code = 'HANDSHAKE_TIMEOUT';
      reject(err);
    }, timeoutMs);

    window.addEventListener('message', onMessage);

    // Post to each allowed origin; the browser delivers a message only to a window
    // whose origin matches the targetOrigin, so at most the real parent responds.
    const send = () => {
      parentOrigins.forEach((origin) => {
        window.parent.postMessage({ type: 'pcs:auth:request', nonce }, origin);
      });
    };
    try {
      send();
      // RE-SEND until answered. A request that arrives before the parent has registered
      // its `message` listener is silently dropped, and the parent only ever REPLIES to a
      // request — it never pushes the token. Verified 2026-09-02 against the Relius
      // ParticipantDashboard.aspx page: a single-shot request timed out every time (the
      // heavy .NET page registers its listener after our SPA has already booted), while
      // re-sending got an instant reply. cleanup() clears this on reply or on timeout.
      retry = setInterval(() => { if (!settled) send(); }, RETRY_MS);
    } catch (err) {
      cleanup();
      reject(err);
    }
  });
}
